import React, { useState, useRef } from 'react';
import {
  Flex, Text, ActionNote, Box, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, step1QuestionsPart2 } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function AlabamaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const step1Form = useRef();
  const step1Pt2Form = useRef();
  const step1InitQ = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitQ);

  const [withhold, setWithhold] = useState(data.withhold_at_highest_rate || false);

  const step1Pt2InitQ = getInitialQuestions(step1QuestionsPart2);
  const [stepOnePtTwoQuestions, setStepOnePtTwoQuestions] = useState(step1Pt2InitQ);

  const renderStep1Form = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Flex column>
        <S.QuestionLabel mb="7px">
          If you claim no personal exemption for yourself and wish to withhold at the highest rate, check here.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={withhold}
                checked={withhold}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setWithhold(!withhold);
                }}
              />
              <Box ml={2} />
              Withhold at the highest rate
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
      { !withhold && (
      <FormConsumer
        questions={stepOnePtTwoQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOnePtTwoQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOnePtTwoQuestions(questions);
        }}
        ref={step1Pt2Form}
      />
      )}
    </Flex>
  );

  const getFilingStatusCode = () => {
    let fsCode;
    switch (stepOnePtTwoQuestions[1].answer.answer.filing_status || data.filing_status) {
      case 'single':
        fsCode = 'S';
        break;
      case 'married_filing_separately':
        fsCode = 'MS';
        break;
      case 'married':
        fsCode = 'M';
        break;
      case 'head_of_household':
        fsCode = 'H';
    }
    return fsCode;
  };

  const totalExemptions = () => {
    const fsCode = getFilingStatusCode();
    const numDependents = stepOnePtTwoQuestions[2].answer.answer.dependents || 0;

    return `${fsCode}-${numDependents}`;
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
        <Flex column mt={10} mb={10}>
          <Flex alignItems="center">
            <Text mr={16}>Download signed documents</Text>
            <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
              <img src="/j/images/onboarding/download.svg" />
            </a>
          </Flex>
        </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let data;
            let err = null;

            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }

            if (err) return;

            const stepOneData = stepOneQuestions
              .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

            // reset any previously saved answers if withhold at highest rate is checked
            const resetStepOnePtTwoQuestions = resetInitialQuestions(step1QuestionsPart2)
              .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

            data = {
              ...stepOneData,
              ...resetStepOnePtTwoQuestions,
              withhold_at_highest_rate: withhold,
              total_exemptions: 0,
            };

            // If withhold at highest rate is not checked
            if (!withhold) {
              if (step1Pt2Form && step1Pt2Form.current) {
                step1Pt2Form.current.validate((error) => {
                  if (error) err = error;
                });
              }

              if (err) return;

              const stepOnePtTwoData = stepOnePtTwoQuestions
                .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              const totalExemptionsTemp = totalExemptions() || '';
              const totalExemptionsData = { total_exemptions: totalExemptionsTemp };

              data = { ...data, ...stepOnePtTwoData, ...totalExemptionsData };
            }

            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>

  );
}

export default AlabamaForm;
